import { useState } from "react";
import { ProfileDownloads } from "@/pages/Reports";
import { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Truck, User as UserIcon, Building2, Download, FileSpreadsheet } from "lucide-react";

const STATUSES = [
  { value: "active",   label: "Active only" },
  { value: "inactive", label: "Inactive only" },
  { value: "all",      label: "Active + inactive" },
];

function ListCard({ title, hint, icon: Icon, pdfHref, xlsxHref, status, onStatus, testidPrefix }) {
  return (
    <Card data-testid={`${testidPrefix}-card`} className="rounded-sm border border-slate-200 shadow-none p-5 flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 inline-flex items-center justify-center rounded-sm bg-slate-900 text-white">
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <div className="font-medium text-slate-900">{title}</div>
          <div className="text-xs text-slate-500 mt-0.5">{hint}</div>
        </div>
      </div>
      <div>
        <Label className="text-xs text-slate-600">Show</Label>
        <Select value={status} onValueChange={onStatus}>
          <SelectTrigger data-testid={`${testidPrefix}-status`} className="mt-1 rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button asChild variant="outline" data-testid={`${testidPrefix}-pdf`} className="rounded-sm border-slate-300">
          <a href={pdfHref(status)} download rel="noopener">
            <Download className="w-4 h-4 mr-1.5" /> PDF
          </a>
        </Button>
        <Button asChild data-testid={`${testidPrefix}-xlsx`} className="rounded-sm bg-slate-900 hover:bg-slate-800">
          <a href={xlsxHref(status)} download rel="noopener">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Excel
          </a>
        </Button>
      </div>
    </Card>
  );
}

export default function ReportProfiles() {
  const [subStatus, setSubStatus] = useState("active");
  const [drvStatus, setDrvStatus] = useState("active");
  const [cliStatus, setCliStatus] = useState("active");

  return (
    <div data-testid="report-profiles-page" className="space-y-10">
      <ProfileDownloads />

      <section data-testid="profile-lists">
        <div className="mb-3">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Reporting · Lists</div>
          <h2 className="font-display text-2xl tracking-tight font-semibold text-slate-900 mt-1">Download lists</h2>
          <p className="text-sm text-slate-600 mt-1">Bulk register downloads — pick a status (active, inactive or both) for each list, then grab a PDF or Excel.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <ListCard
            title="Subcontractors"
            hint="Companies you sub work out to."
            icon={Truck}
            status={subStatus}
            onStatus={setSubStatus}
            testidPrefix="list-sub"
            pdfHref={(s) => fileUrl(`/reports/lists/contacts.pdf?kind=subcontractor&active=${s}`)}
            xlsxHref={(s) => fileUrl(`/reports/lists/contacts.xlsx?kind=subcontractor&active=${s}`)}
          />
          <ListCard
            title="Self-employed drivers"
            hint="Drivers you engage directly."
            icon={UserIcon}
            status={drvStatus}
            onStatus={setDrvStatus}
            testidPrefix="list-drv"
            pdfHref={(s) => fileUrl(`/reports/lists/contacts.pdf?kind=self&active=${s}`)}
            xlsxHref={(s) => fileUrl(`/reports/lists/contacts.xlsx?kind=self&active=${s}`)}
          />
          <ListCard
            title="Clients"
            hint="Companies who pay you."
            icon={Building2}
            status={cliStatus}
            onStatus={setCliStatus}
            testidPrefix="list-cli"
            pdfHref={(s) => fileUrl(`/reports/lists/clients.pdf?active=${s}`)}
            xlsxHref={(s) => fileUrl(`/reports/lists/clients.xlsx?active=${s}`)}
          />
        </div>
      </section>
    </div>
  );
}
